// formatRelativeTime renders an ISO-8601 timestamp (or Date) as a
// compact human-friendly delta — "just now", "3m ago", "2h ago",
// "1d ago". Anything older than ~30 days falls back to the absolute
// date so the PRs list stays readable for stale workflows.
//
// Pure; safe in render. No deps. Returns "" for falsy inputs so the
// caller can render a separator only when there's something to show.
export function formatRelativeTime(input: string | Date | null | undefined): string {
  if (!input) return "";
  const then = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(then.getTime())) return "";

  const deltaSeconds = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));

  if (deltaSeconds < 45) return "just now";
  if (deltaSeconds < 90) return "1m ago";

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) return `${deltaMinutes}m ago`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  if (deltaHours < 24) return `${deltaHours}h ago`;

  const deltaDays = Math.floor(deltaHours / 24);
  if (deltaDays < 30) return `${deltaDays}d ago`;

  return then.toLocaleDateString();
}

// formatRelativeFuture renders a future ISO-8601 timestamp as
// "in 3m", "in 1h", "due now". Returns "" if the input is in the
// past or unparseable. Used for next_tick_at — "sleeping, next tick
// in 4m".
export function formatRelativeFuture(input: string | Date | null | undefined): string {
  if (!input) return "";
  const then = typeof input === "string" ? new Date(input) : input;
  if (Number.isNaN(then.getTime())) return "";

  const deltaSeconds = Math.floor((then.getTime() - Date.now()) / 1000);
  if (deltaSeconds <= 30) return "due now";

  const deltaMinutes = Math.floor(deltaSeconds / 60);
  if (deltaMinutes < 60) return `in ${deltaMinutes}m`;

  const deltaHours = Math.floor(deltaMinutes / 60);
  return `in ${deltaHours}h`;
}

// formatReviewOutcome maps a backend ReviewOutcome enum value to
// the short verb phrase the dashboard renders — "approved", "requested
// changes", "commented", "escalated". Unknown values fall through to
// the raw string.
export function formatReviewOutcome(outcome: string | null | undefined): string {
  switch (outcome) {
    case "approve":
      return "approved";
    case "approve_with_notes":
      return "approved with notes";
    case "request_changes":
      return "requested changes";
    case "comment":
      return "commented";
    case "escalate_to_human":
      return "escalated";
    case "deferred":
      return "deferred";
    default:
      return outcome ?? "";
  }
}
